import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError, switchMap, from } from 'rxjs';
import { AuthStateService } from '@core/services/auth-state.service';
import { CompanyConfigService } from '@core/services/company-config.service';
import { TokenService } from '@core/services/token.service';
import { StorageIntegrityService } from '@core/services/storage-integrity.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private static readonly RETRY_HEADER = 'X-Token-Refresh-Retry';
  private readonly authStateService = inject(AuthStateService);
  private readonly companyConfigService = inject(CompanyConfigService);
  private readonly tokenService = inject(TokenService);
  private readonly storageIntegrityService = inject(StorageIntegrityService);
  private readonly router = inject(Router);

  // Prevent duplicate logout navigations when multiple 401s fail simultaneously.
  private isLoggingOut = false;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.shouldSkipAuth(req.url)) {
      return next.handle(req);
    }

    return from(this.storageIntegrityService.validateIntegrity()).pipe(
      switchMap(isValid => {
        if (!isValid) {
          this.storageIntegrityService.handleTampering();
          return throwError(() => new Error('Storage integrity violation detected'));
        }

        const authReq = this.addAuthHeader(req);

        return next.handle(authReq).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              return this.handle401Error(authReq, next);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private addAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
    const currentCompany = this.authStateService.currentCompany();
    if (!currentCompany) return req;

    const token = this.tokenService.getToken(currentCompany);
    if (!token) return req;

    return req.clone({
      setHeaders: {
        'Authorization': `Bearer ${token}`,
        'X-Company': currentCompany
      }
    });
  }

  /**
   * Handle 401 Unauthorized.
   *
   * TokenService.refreshToken() already deduplicates concurrent calls via its
   * internal refreshInProgress Map — all concurrent 401 handlers share the same
   * Promise and only ONE HTTP tokenRefresh call is ever made. When the Promise
   * resolves, every caller retries its original request automatically.
   */
  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Already retried once with a fresh token → still 401 → session truly expired.
    if (req.headers.get(AuthInterceptor.RETRY_HEADER) === '1') {
      console.warn('[AuthInterceptor] Retry request also got 401 – logging out');
      this.logout();
      return throwError(() => new Error('Session expired'));
    }

    const currentCompany = this.authStateService.currentCompany();
    if (!currentCompany) {
      console.warn('[AuthInterceptor] No active company – logging out');
      this.logout();
      return throwError(() => new Error('Session expired'));
    }

    console.log('[AuthInterceptor] 401 received – refreshing token for', currentCompany);

    return from(this.tokenService.refreshToken(currentCompany)).pipe(
      switchMap(() => {
        // Token refreshed — retry original request once with the new token.
        const retryReq = req.clone({
          setHeaders: { [AuthInterceptor.RETRY_HEADER]: '1' }
        });
        return next.handle(this.addAuthHeader(retryReq));
      }),
      catchError((refreshError) => {
        // Network abort / status-0 are transient — the server may have processed
        // the refresh. Keep the user logged in; the next request will try again.
        const isNetworkError =
          refreshError?.name === 'AbortError' ||
          refreshError?.status === 0 ||
          (refreshError?.error instanceof DOMException && refreshError.error.name === 'AbortError');

        if (isNetworkError) {
          console.warn('[AuthInterceptor] Token refresh aborted by network – will retry on next request');
          return throwError(() => refreshError);
        }

        console.error('[AuthInterceptor] Token refresh failed – logging out:', refreshError);
        this.logout();
        return throwError(() => new Error('Session expired'));
      })
    );
  }

  private logout(): void {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    this.authStateService.reset();
    this.storageIntegrityService.clearState();
    this.companyConfigService.clearAll();
    this.router.navigate(['/auth/login']).finally(() => {
      setTimeout(() => { this.isLoggingOut = false; }, 5000);
    });
  }

  private shouldSkipAuth(url: string): boolean {
    const skipUrls = [
      '/api/CAS/login',
      '/api/CAS/signout',
      '/api/CAS/tokenRefresh',
      '/companies/user/change/password',
      '/api/Authorization/RefreshToken'
    ];
    return skipUrls.some(skipUrl => url.includes(skipUrl));
  }
}
